import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  IEditTask,
  IFranchiseTaskList,
  IParamsGetFranchiseTaskList,
  IParamsGetTaskList,
  ITaskBoard,
  ITaskFile,
  ITaskSelectionList,
} from '~/utils/interface/task';
import {
  handleGetTask,
  handleGetTaskBoard,
  handleGetTaskFiles,
  handleGetTaskList,
  handleGetTaskListFranchise,
  handleGetTaskOfCustomerList,
  handleGetTaskSelection,
} from './taskThunk';
import { DEFAULT_PAGE, ROWS_PER_PAGE } from '~/utils/constants/common';

export interface taskState {
  openAlertModalEdit: boolean;
  openAlertModalDelete: boolean;
  mode: string;
  taskSelectionList: ITaskSelectionList;
  needRefreshData: boolean;
  filterList: IParamsGetTaskList;
  filterTaskList: IParamsGetTaskList;
  filterTaskContactList: IParamsGetTaskList;
  filterFranchiseTaskList: IParamsGetFranchiseTaskList;
  currentPage: number;
  selectedIds: Array<string>;
  totalPages: number;
  taskEdit: IEditTask | null;
  menuSelectedId: string | undefined;
  taskFiles: Array<ITaskFile>;
  taskBoard: Array<ITaskBoard>;
  taskList: Array<IFranchiseTaskList>;
  taskId: string;
}

const initialState: taskState = {
  openAlertModalEdit: false,
  openAlertModalDelete: false,
  currentPage: DEFAULT_PAGE,
  selectedIds: [],
  taskBoard: [],
  taskList: [],
  menuSelectedId: undefined,
  totalPages: 0,
  taskId: '',
  taskFiles: [],
  mode: '',
  taskSelectionList: [],
  needRefreshData: false,
  filterFranchiseTaskList: {
    page: 1,
    limit: ROWS_PER_PAGE,
    sort: [],
  },
  filterList: {
    type: 'list',
    page: 1,
    limit: ROWS_PER_PAGE,
    sort: [],
  },
  filterTaskList: {
    type: 'customer',
    page: 1,
    limit: ROWS_PER_PAGE,
    sort: [],
  },
  filterTaskContactList: {
    type: 'contact',
    page: 1,
    limit: ROWS_PER_PAGE,
    sort: [],
  },
  taskEdit: {},
};

const taskSlice = createSlice({
  name: 'task',
  initialState,
  reducers: {
    changeOpenModalCreate(state) {
      state.taskEdit = null;
    },
    changeOpenModalEdit(state, action: PayloadAction<boolean>) {
      state.openAlertModalEdit = action.payload;
    },
    setFilterList(state, action: PayloadAction<IParamsGetTaskList>) {
      state.filterList = { ...state.filterList, ...action.payload };
    },
    setFilterTaskList(state, action: PayloadAction<IParamsGetTaskList>) {
      state.filterTaskList = { ...state.filterTaskList, ...action.payload };
    },
    setFilterTaskContactList(state, action: PayloadAction<IParamsGetTaskList>) {
      state.filterTaskContactList = { ...state.filterTaskContactList, ...action.payload };
    },
    setFilterFranchiseTaskList(state, action: PayloadAction<IParamsGetFranchiseTaskList>) {
      state.filterFranchiseTaskList = { ...state.filterFranchiseTaskList, ...action.payload };
    },
    changeOpenModalDelete(state, action: PayloadAction<boolean>) {
      state.openAlertModalDelete = action.payload;
      if (!state.openAlertModalDelete) {
        state.menuSelectedId = undefined;
      }
    },
    changeMode(state, action: PayloadAction<string>) {
      state.mode = action.payload;
    },
    changeMenuSelectId(state, action: PayloadAction<string | undefined>) {
      state.menuSelectedId = action.payload;
    },
    changeNeedRefreshData(state, action: PayloadAction<boolean>) {
      state.needRefreshData = action.payload;
    },
    changeTaskId(state, action: PayloadAction<string>) {
      state.taskId = action.payload;
    },
    changeSelectedIds(state, action: PayloadAction<Array<string>>) {
      state.selectedIds = [...action.payload];
    },
  },
  extraReducers(builder) {
    builder.addCase(handleGetTaskSelection.fulfilled, (state, action) => {
      state.taskSelectionList = [...action.payload.responses];
    });
    builder.addCase(handleGetTaskList.fulfilled, (state, action) => {
      state.currentPage = action.payload.pagination.page;
      state.totalPages = action.payload.pagination.totalPages;
      state.needRefreshData = false;
    });
    builder.addCase(handleGetTaskListFranchise.fulfilled, (state, action) => {
      state.taskList = [...action.payload.responses];
      state.currentPage = action.payload.pagination.page;
      state.totalPages = action.payload.pagination.totalPages;
      state.needRefreshData = false;
    });
    builder.addCase(handleGetTaskOfCustomerList.fulfilled, (state, action) => {
      state.currentPage = action.payload.pagination.page;
      state.totalPages = action.payload.pagination.totalPages;
      state.needRefreshData = false;
    });
    builder.addCase(handleGetTask.fulfilled, (state, action) => {
      state.taskEdit = { ...action.payload };
    });

    builder.addCase(handleGetTaskBoard.fulfilled, (state, action) => {
      state.taskBoard = [...action.payload.responses];
    });
    builder.addCase(handleGetTaskFiles.fulfilled, (state, action) => {
      state.taskFiles = [...action.payload.responses];
    });
  },
});

export const taskActions = taskSlice.actions;

const taskReducer = taskSlice.reducer;
export default taskReducer;
